import { db, LocalPlaylist, LocalFavorite, LocalRoutine, LocalUserFavorite } from './index.js';
import { newId } from '../utils/id.js';
import { useAuthStore } from '../stores/authStore.js';
import { accountId, ownedBy } from './scope.js';
import { requestLyricSync } from '../sync/lyricSync.js';
import { LIBRARY_CHANGED_EVENT } from '../sync/events.js';
import { TabSong, TabTrack, Routine, PracticeSession, SessionBlockLog, LyricSheet } from '../types/index.js';

// --- Universal Deletion Staging Cache (4s Undo Window) ---

interface StagedDeletion<T = unknown> {
  item: T;
  timer: ReturnType<typeof setTimeout>;
  commit: () => Promise<void>;
}

const stagedDeletions = new Map<string, StagedDeletion>();

/**
 * Checks whether an item ID is currently staged for deletion.
 */
export function isDeletionStaged(id: string): boolean {
  return stagedDeletions.has(id);
}

/**
 * Stages an item for deletion with an undo window (default 4000ms).
 * The commit function executes permanent deletion from Dexie (and dispatches tombstones)
 * after the timeout expires.
 */
export function stageDeletion<T>(
  id: string,
  item: T,
  commitFn: () => Promise<void>,
  delayMs = 4000
): { cancel: () => void; commitNow: () => Promise<void> } {
  // If there is already a staged deletion for this ID, commit it immediately
  if (stagedDeletions.has(id)) {
    const existing = stagedDeletions.get(id)!;
    clearTimeout(existing.timer);
    existing.commit().catch((err) => console.error('Error committing previous deletion:', err));
    stagedDeletions.delete(id);
  }

  const commit = async () => {
    stagedDeletions.delete(id);
    try {
      await commitFn();
    } catch (err) {
      console.error(`Error executing deletion for ${id}:`, err);
    }
  };

  const timer = setTimeout(commit, delayMs);

  const cancel = () => {
    const staged = stagedDeletions.get(id);
    if (staged) {
      clearTimeout(staged.timer);
      stagedDeletions.delete(id);
    }
  };

  const commitNow = async () => {
    const staged = stagedDeletions.get(id);
    if (staged) {
      clearTimeout(staged.timer);
      await commit();
    }
  };

  stagedDeletions.set(id, {
    item,
    timer,
    commit
  });

  return { cancel, commitNow };
}

/**
 * Cancels a staged deletion and returns the cached item if found.
 */
export function cancelStagedDeletion<T = unknown>(id: string): T | undefined {
  const staged = stagedDeletions.get(id);
  if (staged) {
    clearTimeout(staged.timer);
    stagedDeletions.delete(id);
    return staged.item as T;
  }
  return undefined;
}

/**
 * Flushes all staged deletions immediately (e.g. before page unload).
 */
export async function flushAllStagedDeletions(): Promise<void> {
  const pending = Array.from(stagedDeletions.values());
  stagedDeletions.clear();
  for (const item of pending) {
    clearTimeout(item.timer);
    try {
      await item.commit();
    } catch (err) {
      console.error('Error flushing staged deletion:', err);
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    void flushAllStagedDeletions();
  });
}

// --- Tab Operations ---

export async function saveLocalTab(tab: TabSong, binaryBuffer?: ArrayBuffer): Promise<TabSong> {
  cancelStagedDeletion(tab.id);
  const tabRecord: TabSong = {
    ...tab,
    isDownloaded: Boolean(binaryBuffer || tab.localBlob),
    ...(binaryBuffer && { localBlob: binaryBuffer }),
    // Tab ids are server ids shared by all accounts, so one device keeps a single copy per id: the account
    // that saves it last owns it (the other can re-download from the server).
    ownerId: accountId(),
    createdAt: tab.createdAt || new Date().toISOString()
  };

  await db.tabs.put(tabRecord);
  return tabRecord;
}

export async function getLocalTabs(): Promise<TabSong[]> {
  const uid = accountId();
  const tabs = await db.tabs.toArray();
  return tabs.filter((t) => !t.removedAt && !stagedDeletions.has(t.id) && ownedBy(t, uid));
}

export async function getLocalTabById(id: string): Promise<TabSong | undefined> {
  if (stagedDeletions.has(id)) return undefined;
  const tab = await db.tabs.get(id);
  return tab && !tab.removedAt && ownedBy(tab, accountId()) ? tab : undefined;
}

/**
 * Removes `id` from this account's favorites and playlists (dangling references after a tab delete).
 * Signed-in: a server-known favorite becomes a tombstone and an edited playlist becomes dirty, so the removal
 * also reaches the server (FRE-143). Guests: plain deletes.
 */
async function cleanTabReferences(id: string, uid: string | undefined, serverDeletesTab = false): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', [db.userFavorites, db.playlists], async () => {
    const fav = await db.userFavorites.get([uid ?? '', id]);
    if (fav) {
      // FND-32: when DELETE /api/tabs/:id is going out, the server cascades the favorite row, so no separate
      // favorite DELETE is queued. Otherwise only a server-known favorite needs a DELETE.
      if (uid && fav.serverKnown === 1 && !fav.removedAt && !serverDeletesTab) {
        await db.userFavorites.put({ ...fav, removedAt: now, updatedAt: now, dirty: 1 });
      } else if (!uid || !fav.removedAt) {
        await db.userFavorites.delete([uid ?? '', id]);
      }
    }
    const playlists = (await db.playlists.toArray()).filter((p) => !p.deletedAt && ownedBy(p, uid));
    for (const pl of playlists) {
      if (pl.tabIds.includes(id)) {
        const next: LocalPlaylist = { ...pl, tabIds: pl.tabIds.filter((t) => t !== id), updatedAt: now, dirty: 1 };
        delete next.pushRejectedAt;
        await db.playlists.put(next);
      }
    }
  });
}

/**
 * Library delete (FRE-142 D10), run after the undo window:
 * - never uploaded (`pendingUpload`) or guest scope: hard delete;
 * - own PRIVATE/REJECTED tab on the server: tombstone + `pendingServerDelete` (DELETE /api/tabs/:id is sent by sync);
 * - own PENDING/APPROVED tab: tombstone only (hidden locally, never restored; the server copy stays);
 * - anything else (marketplace download): hard delete.
 * References (favorite, playlist membership) are always cleaned.
 */
export async function deleteLocalTab(id: string): Promise<void> {
  cancelStagedDeletion(id);
  const uid = accountId();
  const stored = await db.tabs.get(id);
  let serverDeletesTab = false;
  if (stored && ownedBy(stored, uid)) {
    const isOwnOnServer =
      uid !== undefined &&
      stored.pendingUpload !== 1 &&
      (stored.uploaderId === uid || stored.status === 'PRIVATE');
    if (isOwnOnServer && (stored.status === 'PRIVATE' || stored.status === 'REJECTED' || stored.status === 'PENDING')) {
      const { localBlob: _blob, ...rest } = stored;
      void _blob;
      await db.tabs.put({
        ...rest,
        isDownloaded: false,
        removedAt: new Date().toISOString(),
        pendingServerDelete: 1
      });
      serverDeletesTab = true;
    } else if (isOwnOnServer && stored.status === 'APPROVED') {
      const { localBlob: _blob, ...rest } = stored;
      void _blob;
      await db.tabs.put({ ...rest, isDownloaded: false, removedAt: new Date().toISOString() });
    } else {
      await db.tabs.delete(id);
    }
  }
  await cleanTabReferences(id, uid, serverDeletesTab);
  try {
    const last = await db.settings.get(`last_opened_tab:${uid ?? 'guest'}`);
    if (last?.value && (last.value.tabId === id || last.value === id)) {
      await db.settings.delete(last.key);
    }
  } catch {
    /* non-fatal */
  }
}

// --- Cloud library helpers (FRE-142) ---

/** Rows queued for upload that may be sent now for `uid`, oldest first. */
export async function getPendingUploads(uid: string, nowIso: string): Promise<TabSong[]> {
  const rows = await db.tabs.where('pendingUpload').equals(1).toArray();
  return rows
    .filter(
      (t) =>
        t.ownerId === uid &&
        !t.removedAt &&
        t.uploadBlocked !== 1 &&
        (!t.nextUploadAt || t.nextUploadAt <= nowIso)
    )
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
}

/** All of `uid`'s rows that are still waiting for their first upload (any backoff / blocked state). */
export async function getAllPendingUploads(uid: string): Promise<TabSong[]> {
  const rows = await db.tabs.where('pendingUpload').equals(1).toArray();
  return rows.filter((t) => t.ownerId === uid && !t.removedAt);
}

/** Own tabs whose server delete is still to be sent. */
export async function getTabTombstonesToPush(uid: string): Promise<TabSong[]> {
  const all = await db.tabs.toArray();
  return all.filter((t) => t.ownerId === uid && t.removedAt && t.pendingServerDelete === 1);
}

export async function hardDeleteLocalTabRow(id: string): Promise<void> {
  await db.tabs.delete(id);
}

/** Stops sending a server delete (409 TAB_NOT_DELETABLE): the tombstone stays as a local hide. */
export async function clearPendingServerDelete(id: string): Promise<void> {
  await db.tabs.update(id, { pendingServerDelete: 0 });
}

/** Records a successful upload. Returns false when the row no longer exists (deleted while uploading). */
export async function markTabUploaded(
  id: string,
  server: { fileUrl?: string; status?: TabSong['status']; uploaderId?: string }
): Promise<boolean> {
  return db.transaction('rw', db.tabs, async () => {
    const cur = await db.tabs.get(id);
    if (!cur) return false;
    const next: TabSong = {
      ...cur,
      fileUrl: server.fileUrl ?? cur.fileUrl,
      status: server.status ?? 'PRIVATE',
      uploaderId: server.uploaderId ?? cur.uploaderId,
      pendingUpload: 0
    };
    delete next.uploadAttempts;
    delete next.nextUploadAt;
    delete next.uploadError;
    delete next.uploadBlocked;
    await db.tabs.put(next);
    return true;
  });
}

export async function recordUploadFailure(
  id: string,
  patch: Pick<TabSong, 'uploadAttempts' | 'nextUploadAt' | 'uploadError' | 'uploadBlocked'>
): Promise<void> {
  await db.tabs.update(id, patch);
}

/** Manual retry: clears the failure state so the next sync run picks the row up. */
export async function retryTabUpload(id: string): Promise<void> {
  await db.transaction('rw', db.tabs, async () => {
    const cur = await db.tabs.get(id);
    if (!cur) return;
    const next: TabSong = { ...cur };
    delete next.uploadBlocked;
    delete next.uploadError;
    delete next.uploadAttempts;
    delete next.nextUploadAt;
    await db.tabs.put(next);
  });
}

/**
 * After a tab was uploaded, mark every routine, playlist and favorite of `uid` that references it dirty so the
 * next push carries the real link (FRE-142 D9, FRE-143).
 */
export async function markTabReferencesDirty(tabId: string, uid: string): Promise<void> {
  const now = new Date().toISOString();
  await db.transaction('rw', [db.routines, db.playlists, db.userFavorites], async () => {
    const routines = await db.routines.toArray();
    for (const r of routines) {
      if (r.deletedAt || (r.ownerId && r.ownerId !== uid)) continue;
      if ((r.blocks ?? []).some((b) => b.tabId === tabId)) {
        await db.routines.put({ ...r, dirty: 1, updatedAt: now });
      }
    }
    const playlists = await db.playlists.toArray();
    for (const p of playlists) {
      if (p.deletedAt || p.ownerId !== uid) continue;
      if (p.tabIds.includes(tabId)) {
        const next: LocalPlaylist = { ...p, dirty: 1, updatedAt: now };
        delete next.pushRejectedAt;
        await db.playlists.put(next);
      }
    }
    const fav = await db.userFavorites.get([uid, tabId]);
    if (fav && !fav.removedAt) await db.userFavorites.put({ ...fav, dirty: 1, updatedAt: now });
  });
}

/** Caches a downloaded file on an existing row of the current account. */
export async function cacheTabBlob(id: string, buffer: ArrayBuffer): Promise<void> {
  const cur = await db.tabs.get(id);
  if (!cur || cur.removedAt || !ownedBy(cur, accountId())) return;
  await db.tabs.update(id, { localBlob: buffer, isDownloaded: true });
}

export interface CloudTabMeta {
  id: string;
  title: string;
  artist: string;
  tuning: string;
  fileUrl: string;
  status: TabSong['status'];
  uploaderId?: string;
  createdAt?: string;
  tracks?: Array<{ trackIndex: number; name: string }>;
}

/**
 * Inserts a metadata-only row (no file) for a server tab that has no local row yet. Never touches an existing
 * row (so a downloaded file or local fields are never overwritten). Returns whether it inserted.
 */
export async function ensureCloudTabMeta(meta: CloudTabMeta, uid: string): Promise<boolean> {
  if (await db.tabs.get(meta.id)) return false;
  const tracks: TabTrack[] | undefined = meta.tracks?.map((t) => ({
    id: `${meta.id}-${t.trackIndex}`,
    tabId: meta.id,
    trackIndex: t.trackIndex,
    name: t.name
  }));
  await db.tabs.put({
    id: meta.id,
    title: meta.title,
    artist: meta.artist,
    tuning: meta.tuning,
    fileUrl: meta.fileUrl,
    status: meta.status,
    uploaderId: meta.uploaderId,
    createdAt: meta.createdAt,
    ...(tracks && { tracks }),
    ownerId: uid,
    isDownloaded: false
  });
  return true;
}

/** Maps an old (re-keyed) tab id to its current id; returns the input when there is no alias. */
export async function resolveTabAlias(id: string): Promise<string> {
  const row = await db.settings.get('tab_alias:' + id);
  return typeof row?.value === 'string' ? row.value : id;
}

/**
 * Finds a local tab whose stored file has the given content hash (used to dedupe imports).
 * Note: favorites/playlist references are only removed on commit (after the undo window), so
 * undoing a staged deletion needs no restore step.
 */
export async function findLocalTabByContentHash(
  hash: string,
  hashFn: (buf: ArrayBuffer) => Promise<string>,
  match: (t: TabSong) => boolean = () => true
): Promise<TabSong | undefined> {
  const tabs = await getLocalTabs();
  for (const t of tabs) {
    if (match(t) && t.localBlob && (await hashFn(t.localBlob)) === hash) return t;
  }
  return undefined;
}

// --- Practice Session Operations ---

/** Signed-in (non-guest) user id; undefined for guests/logged-out. Mirrors routineSync.getCurrentUserId. */
const currentOwnerId = (): string | undefined => {
  const { user, isGuest, token } = useAuthStore.getState();
  return isGuest || !token ? undefined : user?.id;
};

/**
 * Session scoping (FRE-108), mirrors routines: a signed-in user sees their own rows plus unowned
 * (guest-recorded) rows, which that account adopts on its first sync; guests (userId undefined) see only
 * unowned rows. Another user's rows are never visible or uploadable. Rows written while signed in are
 * stamped at write time, and authStore stamps any remaining unowned rows with the outgoing user on
 * logout / account switch, so a previous account's rows can never be adopted by the next one.
 */
const sessionVisibleTo = (s: PracticeSession, userId?: string): boolean =>
  !s.userId || s.userId === userId;

export async function recordPracticeSession(
  clientSessionId: string,
  totalDurationSeconds: number,
  activePlaybackSeconds: number,
  blocks: SessionBlockLog[],
  userId?: string
): Promise<PracticeSession> {
  const session: PracticeSession = {
    clientSessionId,
    userId: userId ?? currentOwnerId(),
    totalDurationSeconds,
    activePlaybackSeconds,
    synced: 0,
    createdAt: new Date().toISOString(),
    blocks
  };

  await db.sessions.put(session);
  return session;
}

/** Stamps unowned rows with the account that adopts them (called before upload). */
export async function claimSessions(userId: string): Promise<void> {
  await db.sessions.filter((s) => !s.userId).modify({ userId });
}

/** Unsynced sessions the given user may upload: their own plus unowned guest rows. Never another user's. */
export async function getUnsyncedSessions(userId?: string): Promise<PracticeSession[]> {
  const rows = await db.sessions.where('synced').equals(0).toArray();
  return rows.filter((s) => sessionVisibleTo(s, userId));
}

export async function countUnsyncedSessions(userId?: string): Promise<number> {
  return (await getUnsyncedSessions(userId)).length;
}

/** Server refused these sessions (VALIDATION / FUTURE_TIMESTAMP): stop retrying but keep the data locally. */
export async function markSessionsRejected(items: Array<{ id: string; reason?: string }>): Promise<void> {
  await db.transaction('rw', db.sessions, async () => {
    for (const it of items) {
      await db.sessions.update(it.id, { synced: 1, syncRejected: it.reason ?? 'REJECTED' });
    }
  });
}

export async function markSessionsSynced(clientSessionIds: string[]): Promise<void> {
  await db.transaction('rw', db.sessions, async () => {
    for (const id of clientSessionIds) {
      await db.sessions.update(id, { synced: 1 });
    }
  });
}

/** Server practice session row as returned by GET /api/sync/sessions. */
export interface ServerPracticeSession {
  id: string;
  userId?: string;
  clientSessionId?: string | null;
  totalDurationSeconds: number;
  activePlaybackSeconds: number;
  createdAt: string;
  blockLogs?: Array<{
    id?: string;
    blockId?: string | null;
    clientBlockId?: string | null;
    category?: string | null;
    achievedBpm: number;
    block?: { category?: string } | null;
  }>;
}

/**
 * Merge the user's server-side sessions into Dexie (synced = 1) so the chart,
 * recent sessions and stats agree offline. Existing local rows are never overwritten.
 */
export async function hydrateServerSessions(serverSessions: ServerPracticeSession[]): Promise<void> {
  const recent = serverSessions.slice(0, 100);
  await db.transaction('rw', db.sessions, async () => {
    for (const ss of recent) {
      const clientSessionId = ss.clientSessionId || `srv-${ss.id}`;
      const existing = await db.sessions.get(clientSessionId);
      if (existing) continue;
      await db.sessions.put({
        clientSessionId,
        userId: ss.userId ?? currentOwnerId(),
        totalDurationSeconds: ss.totalDurationSeconds,
        activePlaybackSeconds: ss.activePlaybackSeconds,
        createdAt: ss.createdAt,
        synced: 1,
        blocks: (ss.blockLogs || []).map((l) => ({
          blockId: l.blockId ?? l.clientBlockId ?? '',
          category: l.block?.category ?? l.category ?? undefined,
          achievedBpm: l.achievedBpm
        }))
      });
    }
  });
}

export async function getAllPracticeSessions(userId?: string): Promise<PracticeSession[]> {
  const all = await db.sessions.orderBy('createdAt').reverse().toArray();
  return all.filter((s) => sessionVisibleTo(s, userId));
}

// --- Routine Operations ---

const isVisibleTo = (r: LocalRoutine, currentUserId?: string): boolean =>
  !r.deletedAt && (!r.ownerId || r.ownerId === currentUserId);

/**
 * Persists a routine edit locally. Sync metadata (ownerId, serverUpdatedAt, legacy, isSample) is
 * taken from the stored record because the page's in-memory copy can be stale. Marks it dirty so
 * the next sync pushes it, and clears any tombstone.
 */
export async function saveLocalRoutine(routine: Routine): Promise<LocalRoutine> {
  cancelStagedDeletion(routine.id);
  return db.transaction('rw', db.routines, async () => {
    const stored = await db.routines.get(routine.id);
    const now = new Date().toISOString();
    const merged: LocalRoutine = {
      ...(stored ?? {}),
      id: routine.id,
      userId: routine.userId ?? stored?.userId,
      // FND-02: stamp the owner at write time so an unsynced routine never leaks to the next account.
      ...((stored?.ownerId ?? currentOwnerId()) ? { ownerId: (stored?.ownerId ?? currentOwnerId()) as string } : {}),
      title: routine.title,
      targetDurationMinutes: routine.targetDurationMinutes,
      blocks: routine.blocks,
      createdAt: stored?.createdAt || routine.createdAt || now,
      updatedAt: now,
      dirty: 1
    };
    delete merged.deletedAt;
    delete merged.pushRejectedAt;
    delete merged.pushRejectedStatus;
    await db.routines.put(merged);
    return merged;
  });
}

/** Visible routines for the current user (guests pass undefined), oldest first. */
export async function getLocalRoutines(currentUserId?: string): Promise<LocalRoutine[]> {
  const all = await db.routines.toArray();
  const visible = all
    .filter((r) => !stagedDeletions.has(r.id) && isVisibleTo(r, currentUserId))
    .sort((a, b) => {
      const ca = a.createdAt ?? '';
      const cb = b.createdAt ?? '';
      if (ca !== cb) return ca < cb ? -1 : 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

  // FND-05: no title dedupe (same-title routines are distinct rows). The only hidden row is a
  // never-synced seeded sample whose title is shadowed by a real (non-sample-placeholder) routine.
  const isPlaceholderSample = (r: LocalRoutine) => r.isSample === 1 && !r.serverUpdatedAt;
  const realTitles = new Set(
    visible.filter((r) => !isPlaceholderSample(r)).map((r) => r.title.trim().toLowerCase())
  );
  return visible.filter(
    (r) => !(isPlaceholderSample(r) && realTitles.has(r.title.trim().toLowerCase()))
  );
}

export async function getLocalRoutineById(id: string): Promise<LocalRoutine | undefined> {
  if (stagedDeletions.has(id)) return undefined;
  const r = await db.routines.get(id);
  return r && !r.deletedAt ? r : undefined;
}

/** Tombstones routines that exist on the server (delete is pushed later); hard-deletes the rest. */
export async function deleteLocalRoutine(id: string): Promise<void> {
  cancelStagedDeletion(id);
  await db.transaction('rw', db.routines, async () => {
    const stored = await db.routines.get(id);
    if (!stored) return;
    if (stored.serverUpdatedAt) {
      const now = new Date().toISOString();
      await db.routines.put({ ...stored, deletedAt: now, updatedAt: now, dirty: 1 });
    } else {
      await db.routines.delete(id);
    }
  });
}

// --- Routine sync helpers (used by src/sync/routineSync.ts) ---

const ownedByOrUnowned = (r: LocalRoutine, userId: string): boolean => !r.ownerId || r.ownerId === userId;

export async function getRoutinesToPush(userId: string): Promise<LocalRoutine[]> {
  const dirty = await db.routines.where('dirty').equals(1).toArray();
  return dirty.filter((r) => !r.deletedAt && ownedByOrUnowned(r, userId) && r.pushRejectedAt !== r.updatedAt);
}

/** Quarantines a routine the server rejected (400/409). Writes only if it was not edited since the snapshot. */
export async function markRoutinePushRejected(id: string, snapshotUpdatedAt: string, status: 400 | 409): Promise<boolean> {
  return db.transaction('rw', db.routines, async () => {
    const cur = await db.routines.get(id);
    if (!cur || cur.updatedAt !== snapshotUpdatedAt) return false;
    await db.routines.put({ ...cur, pushRejectedAt: cur.updatedAt, pushRejectedStatus: status });
    return true;
  });
}

/** User asked to retry a quarantined routine. */
export async function retryRoutinePush(id: string): Promise<void> {
  await db.transaction('rw', db.routines, async () => {
    const cur = await db.routines.get(id);
    if (!cur) return;
    const next: LocalRoutine = { ...cur, dirty: 1 };
    delete next.pushRejectedAt;
    delete next.pushRejectedStatus;
    await db.routines.put(next);
  });
}

/** Copies a (rejected) routine under new ids, owned by `userId`, and hard-deletes the original. */
export async function duplicateLocalRoutine(id: string, userId?: string): Promise<LocalRoutine | undefined> {
  return db.transaction('rw', db.routines, async () => {
    const cur = await db.routines.get(id);
    if (!cur) return undefined;
    const now = new Date().toISOString();
    const copy: LocalRoutine = {
      id: newId(),
      title: cur.title,
      targetDurationMinutes: cur.targetDurationMinutes,
      createdAt: now,
      updatedAt: now,
      dirty: 1,
      ownerId: userId,
      legacy: 0,
      blocks: cur.blocks.map((b) => ({ ...b, id: newId() }))
    };
    copy.blocks = copy.blocks.map((b) => ({ ...b, routineId: copy.id }));
    await db.routines.delete(id);
    await db.routines.put(copy);
    return copy;
  });
}

export async function getRoutineTombstones(userId: string): Promise<LocalRoutine[]> {
  const all = await db.routines.toArray();
  return all.filter((r) => r.deletedAt && ownedByOrUnowned(r, userId));
}

export async function hardDeleteLocalRoutine(id: string): Promise<void> {
  await db.routines.delete(id);
}

/**
 * Records a successful PUT. Returns 'gone' when the routine was deleted locally while the request
 * was in flight (caller should send a DELETE), otherwise 'ok'. The routine stays dirty if it was
 * edited again after the snapshot was taken.
 */
export async function markRoutinePushed(
  id: string,
  snapshotUpdatedAt: string,
  serverUpdatedAt: string,
  userId: string
): Promise<'ok' | 'gone'> {
  return db.transaction('rw', db.routines, async () => {
    const cur = await db.routines.get(id);
    if (!cur) return 'gone';
    await db.routines.put({
      ...cur,
      ownerId: userId,
      serverUpdatedAt,
      legacy: 0,
      dirty: cur.updatedAt === snapshotUpdatedAt ? 0 : 1
    });
    return 'ok';
  });
}

// --- Sample routine seeding ---

const SAMPLE_SEEDED_KEY = 'fretboard_sample_routine_seeded';

const buildSampleRoutine = (): LocalRoutine => {
  const now = new Date().toISOString();
  return {
    id: newId(),
    title: 'روتین گرم‌کردن و استقامت روزانه',
    targetDurationMinutes: 35,
    createdAt: now,
    updatedAt: now,
    dirty: 0,
    isSample: 1,
    blocks: [
      {
        id: newId(),
        category: 'WARMUP',
        durationMinutes: 10,
        notes: 'تمرینات کروماتیک ۱-۲-۳-۴ روی تمام سیم‌ها با مضراب متناوب',
        targetBpm: 100
      },
      {
        id: newId(),
        category: 'REPERTOIRE',
        durationMinutes: 15,
        notes: 'اجرای ریف اصلی و سولو آهنگ انتخابی',
        targetBpm: 120
      },
      {
        id: newId(),
        category: 'TONE_DIALING',
        durationMinutes: 10,
        notes: 'تنظیم افکت‌ها و فیلترهای گین و اکولایزر امپلی‌فایر'
      }
    ]
  };
};

let ensureSamplePromise: Promise<void> | null = null;
// In-memory fallback for the localStorage flag (storage can throw in private modes / when blocked).
let sampleSeedDecidedThisSession = false;

const readSeededFlag = (): boolean => {
  try {
    return localStorage.getItem(SAMPLE_SEEDED_KEY) !== null;
  } catch {
    return false;
  }
};

const writeSeededFlag = (): void => {
  try {
    localStorage.setItem(SAMPLE_SEEDED_KEY, '1');
  } catch {
    // Storage unavailable: the in-memory flag still prevents resurrection for this session.
  }
};

/**
 * Idempotent: saves the sample routine to Dexie only on first run, when there are no routines.
 * - Concurrent calls (StrictMode double effects, Dashboard + Architect) share one in-flight promise.
 * - The check + insert runs in one Dexie rw transaction, so other tabs cannot create a duplicate.
 * - A localStorage flag records that seeding was already decided, so a sample the user
 *   deleted (leaving zero routines) is not resurrected. Clearing site data resets it.
 */
export function ensureSampleRoutine(): Promise<void> {
  if (!ensureSamplePromise) {
    ensureSamplePromise = (async () => {
      try {
        if (sampleSeedDecidedThisSession || readSeededFlag()) return;
        await db.transaction('rw', db.routines, async () => {
          const sample = buildSampleRoutine();
          const exists = await db.routines.where('title').equals(sample.title).first();
          if (!exists && (await db.routines.count()) === 0) {
            await db.routines.put(sample);
          }
        });
        sampleSeedDecidedThisSession = true;
        writeSeededFlag();
      } catch (err) {
        console.error('Failed to ensure sample routine:', err);
      } finally {
        ensureSamplePromise = null;
      }
    })();
  }
  return ensureSamplePromise;
}

// --- Favorites Operations ---

/**
 * Toggles the local favorite state (FRE-143: desired-state rows, synced as on/off).
 * Signed in: on = dirty row (keeps `serverKnown`), off = tombstone. Guest: on = row, off = delete.
 * Returns the new state.
 */
export async function toggleFavoriteTab(tabId: string): Promise<boolean> {
  const uid = accountId();
  const owner = uid ?? '';
  return db.transaction('rw', db.userFavorites, async () => {
    const existing = await db.userFavorites.get([owner, tabId]);
    const now = new Date().toISOString();
    const isOn = Boolean(existing && !existing.removedAt);
    if (isOn) {
      if (uid) {
        await db.userFavorites.put({ ...existing!, removedAt: now, updatedAt: now, dirty: 1 });
      } else {
        await db.userFavorites.delete([owner, tabId]);
      }
      return false;
    }
    const next: LocalUserFavorite = {
      ownerId: owner,
      tabId,
      addedAt: now,
      updatedAt: now,
      dirty: 1,
      serverKnown: existing?.serverKnown ?? 0
    };
    await db.userFavorites.put(next);
    return true;
  });
}

export async function isTabFavorited(tabId: string): Promise<boolean> {
  const row = await db.userFavorites.get([accountId() ?? '', tabId]);
  return Boolean(row && !row.removedAt);
}

export async function getLocalFavorites(): Promise<LocalFavorite[]> {
  const rows = await db.userFavorites.where('ownerId').equals(accountId() ?? '').toArray();
  return rows.filter((r) => !r.removedAt).map(({ tabId, addedAt }) => ({ tabId, addedAt }));
}

/** Favorite rows (including tombstones) of `uid` whose state the server does not have yet. */
export async function getFavoritesToPush(uid: string): Promise<LocalUserFavorite[]> {
  const rows = await db.userFavorites.where('ownerId').equals(uid).toArray();
  return rows.filter((r) => r.dirty === 1);
}

/**
 * Records the outcome of a favorite push. `snapshot` is the row's `updatedAt` when the request was built:
 * if the row changed meanwhile it stays dirty. 'on' = PUT ok, 'removed' = DELETE ok, 'local' = the server
 * refused (404/400): stays a local-only favorite, no retry loop.
 */
export async function markFavoritePushed(
  uid: string,
  tabId: string,
  snapshot: string,
  result: 'on' | 'removed' | 'local'
): Promise<void> {
  await db.transaction('rw', db.userFavorites, async () => {
    const cur = await db.userFavorites.get([uid, tabId]);
    if (!cur) return;
    const unchanged = cur.updatedAt === snapshot;
    if (result === 'removed') {
      if (unchanged) await db.userFavorites.delete([uid, tabId]);
      else await db.userFavorites.put({ ...cur, serverKnown: 0 });
    } else if (result === 'on') {
      await db.userFavorites.put({ ...cur, serverKnown: 1, dirty: unchanged ? 0 : 1 });
    } else {
      await db.userFavorites.put({ ...cur, serverKnown: 0, dirty: unchanged ? 0 : 1 });
    }
  });
}

// --- Last opened tab (per account) ---

const lastOpenedKey = (): string => `last_opened_tab:${accountId() ?? 'guest'}`;

export async function getLastOpenedTabSetting(): Promise<{ key: string; value: any } | undefined> {
  return db.settings.get(lastOpenedKey());
}

export async function clearLastOpenedTabSetting(): Promise<void> {
  await db.settings.delete(lastOpenedKey());
}

export async function setLastOpenedTabSetting(value: unknown): Promise<void> {
  await db.settings.put({ key: lastOpenedKey(), value });
}

// --- Playlist Operations ---

async function getOwnedPlaylist(id: string): Promise<LocalPlaylist | undefined> {
  const pl = await db.playlists.get(id);
  return pl && !pl.deletedAt && ownedBy(pl, accountId()) ? pl : undefined;
}

/** Marks `pl` as locally edited (FRE-143): bumps `updatedAt`, dirty, and clears a previous push rejection. */
function touchPlaylist(pl: LocalPlaylist): LocalPlaylist {
  const next: LocalPlaylist = { ...pl, updatedAt: new Date().toISOString(), dirty: 1 };
  delete next.pushRejectedAt;
  return next;
}

type PlaylistInput = Pick<LocalPlaylist, 'id' | 'title' | 'createdAt' | 'tabIds'> & Partial<LocalPlaylist>;

/**
 * Persists a playlist locally. Sync metadata (serverUpdatedAt) comes from the stored row because the page's
 * in-memory copy can be stale. Marks it dirty and clears any tombstone.
 */
export async function saveLocalPlaylist(playlist: PlaylistInput): Promise<LocalPlaylist> {
  cancelStagedDeletion(playlist.id);
  return db.transaction('rw', db.playlists, async () => {
    const stored = await db.playlists.get(playlist.id);
    const merged: LocalPlaylist = touchPlaylist({
      ...(playlist as LocalPlaylist),
      ownerId: accountId(),
      serverUpdatedAt: stored?.serverUpdatedAt ?? playlist.serverUpdatedAt,
      updatedAt: '',
      dirty: 1
    });
    delete merged.deletedAt;
    if (merged.serverUpdatedAt === undefined) delete merged.serverUpdatedAt;
    await db.playlists.put(merged);
    return merged;
  });
}

export async function getLocalPlaylists(): Promise<LocalPlaylist[]> {
  const uid = accountId();
  const playlists = await db.playlists.toArray();
  return playlists.filter((p) => !p.deletedAt && !stagedDeletions.has(p.id) && ownedBy(p, uid));
}

export async function renameLocalPlaylist(id: string, title: string): Promise<LocalPlaylist | undefined> {
  return db.transaction('rw', db.playlists, async () => {
    const playlist = await getOwnedPlaylist(id);
    if (!playlist) return undefined;
    const renamed = touchPlaylist({ ...playlist, title });
    await db.playlists.put(renamed);
    return renamed;
  });
}

/** Tombstones playlists that exist on the server (DELETE is pushed later); hard-deletes the rest. */
export async function deleteLocalPlaylist(id: string): Promise<void> {
  cancelStagedDeletion(id);
  await db.transaction('rw', db.playlists, async () => {
    const stored = await getOwnedPlaylist(id);
    if (!stored) return;
    if (stored.serverUpdatedAt) {
      const now = new Date().toISOString();
      await db.playlists.put({ ...stored, deletedAt: now, updatedAt: now, dirty: 1 });
    } else {
      await db.playlists.delete(id);
    }
  });
}

export async function addTabToPlaylist(playlistId: string, tabId: string): Promise<void> {
  await db.transaction('rw', db.playlists, async () => {
    const playlist = await getOwnedPlaylist(playlistId);
    if (playlist && !playlist.tabIds.includes(tabId)) {
      await db.playlists.put(touchPlaylist({ ...playlist, tabIds: [...playlist.tabIds, tabId] }));
    }
  });
}

export async function removeTabFromPlaylist(playlistId: string, tabId: string): Promise<void> {
  await db.transaction('rw', db.playlists, async () => {
    const playlist = await getOwnedPlaylist(playlistId);
    if (playlist) {
      await db.playlists.put(touchPlaylist({ ...playlist, tabIds: playlist.tabIds.filter((id) => id !== tabId) }));
    }
  });
}

// --- Playlist sync helpers (used by src/sync/playlistSync.ts) ---

export async function getPlaylistsToPush(uid: string): Promise<LocalPlaylist[]> {
  const dirty = await db.playlists.where('dirty').equals(1).toArray();
  return dirty.filter((p) => !p.deletedAt && p.ownerId === uid);
}

export async function getPlaylistTombstones(uid: string): Promise<LocalPlaylist[]> {
  const all = await db.playlists.toArray();
  return all.filter((p) => p.deletedAt && p.ownerId === uid);
}

export async function hardDeleteLocalPlaylist(id: string): Promise<void> {
  await db.playlists.delete(id);
}

/**
 * Records a successful PUT. 'gone' = deleted locally while the request was in flight (caller sends DELETE).
 * Stays dirty when edited again after the snapshot.
 */
export async function markPlaylistPushed(
  id: string,
  snapshotUpdatedAt: string,
  serverUpdatedAt: string,
  uid: string
): Promise<'ok' | 'gone'> {
  return db.transaction('rw', db.playlists, async () => {
    const cur = await db.playlists.get(id);
    if (!cur) return 'gone';
    if (cur.deletedAt) return 'gone';
    await db.playlists.put({
      ...cur,
      ownerId: uid,
      serverUpdatedAt,
      dirty: cur.updatedAt === snapshotUpdatedAt ? 0 : 1
    });
    return 'ok';
  });
}

/** Dirty favorites + dirty/tombstoned playlists of `uid` (for the pending-library counter). */
export async function countDirtyLibrary(uid: string): Promise<number> {
  const favs = (await getFavoritesToPush(uid)).length;
  const pls = (await getPlaylistsToPush(uid)).length + (await getPlaylistTombstones(uid)).length;
  const lyrics = (await getLyricSheetsToPush(uid)).length + (await getLyricSheetTombstones(uid)).length;
  return favs + pls + lyrics;
}

// --- Chord lyric sheets (local only; per-account scope like favorites/playlists) ---

/** Live (not deleted) sheets linked to a tab for the current account/guest scope, newest edit first. */
export async function getLyricSheetsForTab(tabId: string): Promise<LyricSheet[]> {
  const uid = accountId();
  const rows = await db.lyricSheets.where('tabId').equals(tabId).toArray();
  return rows
    .filter((r) => !r.deletedAt && ownedBy(r, uid))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

export async function getLyricSheetById(id: string): Promise<LyricSheet | undefined> {
  const r = await db.lyricSheets.get(id);
  return r && !r.deletedAt && ownedBy(r, accountId()) ? r : undefined;
}

export interface LyricSheetInput {
  id?: string;
  tabId: string | null;
  title: string;
  content: string;
}

/** Creates or updates a sheet. The owner is stamped at first write and never changes after that. */
export async function saveLyricSheet(input: LyricSheetInput): Promise<LyricSheet> {
  const now = new Date().toISOString();
  const id = input.id ?? newId();
  return db.transaction('rw', db.lyricSheets, async () => {
    const stored = await db.lyricSheets.get(id);
    const ownerId = stored ? stored.ownerId : accountId();
    const row: LyricSheet = {
      id,
      tabId: input.tabId,
      title: input.title,
      content: input.content,
      createdAt: stored?.createdAt ?? now,
      updatedAt: now,
      dirty: 1,
      ...(stored?.serverUpdatedAt ? { serverUpdatedAt: stored.serverUpdatedAt } : {}),
      ...(ownerId ? { ownerId } : {})
    };
    await db.lyricSheets.put(row);
    return row;
  }).then((row) => {
    lyricChanged();
    return row;
  });
}

/** Refreshes the pending counter and schedules a push (no-op offline / as guest). */
function lyricChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
  requestLyricSync();
}

/** Soft delete: the row stays as a tombstone so a later sync phase can push the deletion. */
export async function deleteLyricSheet(id: string): Promise<void> {
  const stored = await db.lyricSheets.get(id);
  if (!stored || !ownedBy(stored, accountId())) return;
  const now = new Date().toISOString();
  const next = { ...stored, deletedAt: now, updatedAt: now, dirty: 1 as const };
  delete next.pushRejectedAt;
  await db.lyricSheets.put(next);
  lyricChanged();
}

// --- Lyric sheet sync helpers (used by src/sync/lyricSync.ts) ---

export async function getLyricSheetsToPush(uid: string): Promise<LyricSheet[]> {
  const dirty = await db.lyricSheets.where('dirty').equals(1).toArray();
  return dirty.filter((r) => !r.deletedAt && r.ownerId === uid);
}

export async function getLyricSheetTombstones(uid: string): Promise<LyricSheet[]> {
  const all = await db.lyricSheets.toArray();
  return all.filter((r) => r.deletedAt && r.ownerId === uid);
}

/**
 * Records a successful PUT. 'gone' = deleted locally while the request was in flight (caller sends DELETE).
 * Stays dirty when edited again after the snapshot.
 */
export async function markLyricSheetPushed(
  id: string,
  snapshotUpdatedAt: string,
  serverUpdatedAt: string,
  uid: string
): Promise<'ok' | 'gone'> {
  return db.transaction('rw', db.lyricSheets, async () => {
    const cur = await db.lyricSheets.get(id);
    if (!cur || cur.deletedAt) return 'gone';
    await db.lyricSheets.put({
      ...cur,
      ownerId: uid,
      serverUpdatedAt,
      dirty: cur.updatedAt === snapshotUpdatedAt ? 0 : 1
    });
    return 'ok';
  });
}

// Reading-mode preferences (device-wide, not per account)
const LYRIC_FONT_KEY = 'lyric_font_size';

export async function getLyricFontSize(): Promise<number | undefined> {
  const row = await db.settings.get(LYRIC_FONT_KEY);
  return typeof row?.value === 'number' ? row.value : undefined;
}

export async function setLyricFontSize(px: number): Promise<void> {
  await db.settings.put({ key: LYRIC_FONT_KEY, value: px });
}
